import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Niivue, SLICE_TYPE } from '@niivue/niivue';
import { fetchBlob, ApiError } from '../api';
import './MriViewer.css';

/**
 * Side-by-side baseline / follow-up MRI viewer using NiiVue.
 *
 * Two modes:
 *
 *   1. Demo mode (Dashboard)
 *      <MriViewer demoId="DEMO_001" user={user} onAuthError={...} />
 *      The viewer fetches both volumes itself from
 *        /api/scan/{demoId}/baseline
 *        /api/scan/{demoId}/followup
 *
 *   2. Direct-URL mode (MyCases)
 *      <MriViewer baselineUrl={blobUrl} followupUrl={blobUrl} />
 *      The parent has already fetched the blobs (from the user-cases
 *      endpoints, which live under a different path) and just hands the
 *      blob URLs in. The viewer skips fetching entirely and renders.
 *
 * Graceful fallbacks (demo mode only):
 *   - 404 / 501            → "MRI viewer not yet enabled on the server"
 *   - 401                  → caller handles redirect to /login
 *   - any other error      → red error banner
 */
export default function MriViewer({
  demoId,
  user,
  onAuthError,
  baselineUrl: directBaselineUrl,
  followupUrl: directFollowupUrl,
}) {
  // Mode detection. We check `!== undefined` (rather than truthy) so that
  // passing null explicitly — e.g. a case with no follow-up yet — still
  // counts as direct mode.
  const directMode =
    directBaselineUrl !== undefined || directFollowupUrl !== undefined;

  // State only used in demo (self-fetch) mode.
  const [fetchedBaselineUrl, setFetchedBaselineUrl] = useState(null);
  const [fetchedFollowupUrl, setFetchedFollowupUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  // Demo-mode fetch effect. Bails out immediately in direct mode.
  useEffect(() => {
    if (directMode) return;
    if (!demoId || !user) return;

    let cancelled = false;
    let createdUrls = [];

    setLoading(true);
    setError(null);
    setUnavailable(false);
    setFetchedBaselineUrl(null);
    setFetchedFollowupUrl(null);

    (async () => {
      try {
        const [bl, fu] = await Promise.all([
          fetchBlob(`/api/scan/${encodeURIComponent(demoId)}/baseline`, user),
          fetchBlob(`/api/scan/${encodeURIComponent(demoId)}/followup`, user),
        ]);
        createdUrls = [bl, fu];

        if (cancelled) {
          createdUrls.forEach((u) => URL.revokeObjectURL(u));
          return;
        }

        setFetchedBaselineUrl(bl);
        setFetchedFollowupUrl(fu);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 401) {
            if (onAuthError) onAuthError();
            return;
          }
          if (err.status === 404 || err.status === 501) {
            setUnavailable(true);
            return;
          }
          setError(err.detail || err.message);
          return;
        }
        setError('Could not load the MRI scans.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [demoId, user, onAuthError, directMode]);

  // Pick the URLs that actually go to the canvases.
  const baselineUrl = directMode ? directBaselineUrl : fetchedBaselineUrl;
  const followupUrl = directMode ? directFollowupUrl : fetchedFollowupUrl;

  // Render nothing if there's no work to do (and no IDs passed).
  if (!directMode && !demoId) return null;

  if (unavailable) {
    return (
      <div className="mri-viewer mri-viewer--unavailable">
        <div className="mri-viewer__icon" aria-hidden>🧠</div>
        <div>
          <strong>MRI viewer not yet enabled.</strong>
          <div className="mri-viewer__hint">
            The scan endpoint is not deployed yet on the server.
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mri-viewer mri-viewer--error">
        <strong>Could not load the MRI scans:</strong> {error}
      </div>
    );
  }

  return (
    <div className="mri-viewer">
      <div className="mri-viewer__pair">
        <NiiPane url={baselineUrl} label="Baseline" loading={loading} />
        <NiiPane url={followupUrl} label="Follow-up" loading={loading} />
      </div>
      <div className="mri-viewer__hint mri-viewer__hint--inline">
        Tip: click and drag on a panel to scroll through slices.
        Right-click drag adjusts contrast (window/level).
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Single NiiVue canvas                                                */
/* ------------------------------------------------------------------ */

function NiiPane({ url, label, loading }) {
  const canvasRef = useRef(null);
  const nvRef = useRef(null);
  const [paneError, setPaneError] = useState(null);
  const [paneLoading, setPaneLoading] = useState(false);

  // Initialize NiiVue once per pane
  const initNiivue = useCallback(() => {
    if (!canvasRef.current || nvRef.current) return;
    try {
      const nv = new Niivue({
        backColor: [0.04, 0.04, 0.06, 1],
        crosshairColor: [0.95, 0.65, 0.24, 1], // BrainAxis gold
        crosshairWidth: 1,
        isColorbar: false,
        isOrientCube: false,
        show3Dcrosshair: false,
        sliceType: SLICE_TYPE.MULTIPLANAR,
        textHeight: 0.04,
        fontMinPx: 12,
        loadingText: '',
      });
      nv.attachToCanvas(canvasRef.current);
      nvRef.current = nv;
    } catch (err) {
      setPaneError('Failed to initialize the viewer.');
    }
  }, []);

  useEffect(() => {
    initNiivue();
    return () => {
      // NiiVue doesn't expose a clean teardown; null the ref so the next
      // mount can rebuild if needed.
      nvRef.current = null;
    };
  }, [initNiivue]);

  // Load (or replace) the volume when the URL changes
  useEffect(() => {
    if (!url) return;
    if (!nvRef.current) {
      initNiivue();
      if (!nvRef.current) return;
    }

    let cancelled = false;
    setPaneError(null);
    setPaneLoading(true);

    (async () => {
      try {
        await nvRef.current.loadVolumes([{ url, name: 'scan.nii.gz' }]);
      } catch (err) {
        if (!cancelled) {
          setPaneError('Could not display this scan.');
        }
      } finally {
        if (!cancelled) setPaneLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, initNiivue]);

  return (
    <div className="nii-pane">
      <div className="nii-pane__label">{label}</div>
      <div className="nii-pane__canvas-wrap">
        <canvas ref={canvasRef} className="nii-pane__canvas" />
        {(loading || paneLoading) && !paneError && (
          <div className="nii-pane__overlay">Loading scan…</div>
        )}
        {paneError && (
          <div className="nii-pane__overlay nii-pane__overlay--error">
            {paneError}
          </div>
        )}
      </div>
    </div>
  );
}
